// import { routes } from './app.routes';
import { Routes } from '@angular/router';
import { LoginComponent } from './pages/auth/login/login.component';
import { authGuard } from './guards/auth.guard';
import { MainLayoutComponent } from './layout/main-layout/main-layout.component';
import { ResetPasswordComponent } from './pages/auth/reset-password/reset-password.component';
import { AuthenticatorComponent } from './pages/auth/authenticator/authenticator.component';
import { LoginRedirectGuard } from './guards/loginGuard/login-redirect.guard';
import { ParentMailComponent } from './features/mailbox/components/parent-mail/parent-mail/parent-mail.component';
import { CampaignMailPreviewComponent } from './shared/campaign-mail-preview/campaign-mail-preview.component';
import { UnsubscribeMailComponent } from './shared/unsubscribe-mail/unsubscribe-mail.component';
import { UnauthorizedComponent } from './pages/unauthorized/unauthorized.component';
import { permissionGuard } from './guards/permision-guard/permission.guard';
import { permissionEntity } from './core/constants/permission';
import { ImagePreviewPageComponent } from './pages/image-preview-page/image-preview-page.component';
import { PdfPreviewPageComponent } from './pages/pdf-preview-page/pdf-preview-page.component';
import { StartDayComponent } from './pages/start-day/start-day.component';
import { SignOffWrapupComponent } from './shared/sign-off-wrapup/sign-off-wrapup.component';
import { startDayGuard } from './guards/start-day/start-day.guard';
import { EmailResetPasswordComponent } from './pages/auth/email-reset-password/email-reset-password.component';

export const routes: Routes = [
  { path: 'login', component: LoginComponent, canActivate: [LoginRedirectGuard], title: 'Login' },
  { path: 'reset-password', component: ResetPasswordComponent, title: 'Reset Password' },
  { path: 'authenticator', component: AuthenticatorComponent, title: 'Authenticator' },
  { path: 'mail', component: ParentMailComponent, title: 'Crizac Mail' },
  { path: 'admin', component: LoginComponent, canActivate: [LoginRedirectGuard], title: 'Admin' },
  { path: 'admin/authenticator', component: AuthenticatorComponent, title: 'Authenticator' },
  { path: 'start-day', component: StartDayComponent, title: 'Start Your Day' },
  // Note: away-lock is no longer a routed page — it renders as an app-wide overlay
  // (see app.component.ts) driven by TmsSocketService.overlayVisible$, so in-progress
  // form state on the underlying page survives lock/unlock. Any stale '/away-lock'
  // URL (old bookmark/tab from before this change) falls through to the wildcard
  // route below, which redirects to '/login' — LoginRedirectGuard then bounces an
  // already-authenticated user straight to the dashboard, where the overlay picks
  // up normally from isLocked.
  { path: 'sign-off-wrapup', component: SignOffWrapupComponent, canActivate: [authGuard], title: 'Wrap Up' },
  { path: 'email-reset-password', component: EmailResetPasswordComponent, title: 'Reset Password' },

  {
    path: 'dashboard',
    component: MainLayoutComponent,
    canActivate: [authGuard, permissionGuard, startDayGuard],
    // canActivate: [authGuard, permissionGuard],
    data: { permission: permissionEntity.modules.dashboard },
    title: 'Dashboard',
    children: [
      {
        path: 'rm-dashboard',
        data: { permission: permissionEntity.modules.dashboardrm },
        canActivate: [authGuard, permissionGuard],
        loadChildren: () => import('./features/rm-dashboard/rm-dashboard.module').then(m => m.RmDashboardModule),
      },
      {
        path: 'mm-dashboard',
        canActivate: [authGuard, permissionGuard],
        data: { permission: permissionEntity.modules.dashboardmm },
        loadChildren: () => import('./features/mm-dash-board/mm-dashboard.module').then(m => m.MmDashboardModule),
      },
      {
        path: 'application-dashboard',
        canActivate: [authGuard, permissionGuard],
        data: { permission: permissionEntity.modules.application_dashboard },
        loadChildren: () => import('./features/application-dashboard/application-dashboard.module').then(m => m.ApplicationDashboardModule),
      },
      {
        path: 'africa-dashboard',
        canActivate: [authGuard, permissionGuard],
        data: { permission: permissionEntity.modules.africa_dashboard },
        loadChildren: () => import('./features/africa-dashboard/africa-dashboard.module').then(m => m.AfricaDashboardModule),
      },
      {
        path: 'all-application',
        canActivate: [authGuard, permissionGuard],
        data: { permission: permissionEntity.modules.applications },
        loadChildren: () => import('./features/all-application/all-application.module').then(m => m.AllApplicationModule),
      },
      {
        path: 'uk-mock-interview',
        canActivate: [authGuard, permissionGuard],
        data: { permission: permissionEntity.modules.uk_mock_interview },
        loadChildren: () => import('./features/uk-mock-interview/uk-mock-interview.module').then(m => m.UkMockInterviewModule),
      },
      {
        path: 'art',
        canActivate: [authGuard, permissionGuard],
        data: { permission: permissionEntity.modules.art_received },
        loadChildren: () => import('./features/art-application/art-application.module').then(m => m.ArtApplicationModule),
      },
      {
        path: 'data',
        canActivate: [authGuard, permissionGuard],
        data: { permission: permissionEntity.modules.data_entry_application },
        loadChildren: () => import('./features/data-entry-applicaton/data-entry-application.module').then(m => m.DataEntryApplicationModule),
      },
      {
        path: 'agent',
        canActivate: [authGuard, permissionGuard],
        data: { permission: permissionEntity.modules.agents },
        loadChildren: () => import('./features/agent/agent.module').then(m => m.AgentModule),
      },
      {
        path: 'notice',
        canActivate: [authGuard],
        loadChildren: () => import('./features/notice/notice-board.module').then(m => m.NoticeBoardModule),
      },
      {
        path: 'counsellor',
        canActivate: [authGuard],
        loadChildren: () => import('./features/counsellor/counsellor.module').then(m => m.CounsellorModule),
      },
      {
        path: 'mailbox',
        canActivate: [authGuard],
        loadChildren: () => import('./features/mailbox/mailbox.module').then(m => m.MailboxModule),
      },
      // {
      //   path: 'agent',
      //   loadChildren: () => import('./features/agent/agent.routes').then(m => m.AGENT_ROUTES),
      // },
      { path: '', redirectTo: 'all-application', pathMatch: 'full' },
      {
        path: 'chat-master',
        canActivate: [authGuard],
        loadChildren: () => import('./features/chatmaster/chatmaster.module').then(m => m.ChatmasterModule),
      },
      {
        path: 'cct',
        canActivate: [authGuard, permissionGuard],
        data: { permission: permissionEntity.modules.cct_interview_master },
        loadChildren: () => import('./features/student-interview/student-interview.module').then(m => m.StudentInterviewModule),
      },
      {
        path: 'enroly-logs',
        canActivate: [authGuard],
        loadChildren: () => import('./features/enroly-logs/enroly-logs.module').then(m => m.EnrolyLogsModule),
      },
      // {
      //   path: 'file-manager',
      //   canActivate: [authGuard],
      //   loadChildren: () => import('./features/file-manager/file-manager.module').then(m => m.FileManagerModule),
      // },
      // {
      //   path: 'file-manager-2',
      //   canActivate: [authGuard, permissionGuard],
      //   data: { permission: permissionEntity.modules.my_drive },
      //   loadChildren: () => import('./features/file-manager-2/file-manager.module').then(m => m.FileManagerModule),
      // },
      {
        path: '',
        canActivate: [authGuard],
        loadChildren: () => import('./features/reports/reports.module').then(m => m.ReportsModule),
      },
      {
        path: '',
        canActivate: [authGuard],
        loadChildren: () => import('./features/course-master/course-master.module').then(m => m.CourseMasterModule),
      },
      {
        path: '',
        canActivate: [authGuard],
        loadChildren: () => import('./features/university-master/university-master.module').then(m => m.UniversityMasterModule),
      },
      {
        path: 'automation',
        canActivate: [authGuard, permissionGuard],
        data: { permission: permissionEntity.modules.ai_automation },
        loadChildren: () => import('./features/autoMation/automation.module').then(m => m.AutomationModule),
      },
      {
        path: '',
        canActivate: [authGuard],
        loadChildren: () => import('./features/accommodation/accommodation.module').then(m => m.AccommodationModule),
      },
      {
        path: '',
        canActivate: [authGuard],
        loadChildren: () => import('./features/admin-master/admin-master.module').then(m => m.AdminMasterModule),
      },
      {
        path: '',
        canActivate: [authGuard],
        loadChildren: () => import('./features/user-profile/user-profile.module').then(m => m.UserProfileModule),
      },
      {
        path: 'cpt',
        canActivate: [authGuard, permissionGuard],
        data: { permission: permissionEntity.modules.cpt_document_verification },
        loadChildren: () => import('./features/cpt/cpt.module').then(m => m.CptModule),
      },
      {
        path: 'application-status',
        canActivate: [authGuard, permissionGuard],
        data: { permission: permissionEntity.modules.application_primary_status },
        loadChildren: () => import('./features/application-status/applicaton-status.module').then(m => m.ApplicatonStatusModule),
      },
      {
        path: 'automation',
        canActivate: [authGuard],
        loadChildren: () => import('./features/automation-forms/automation-forms.module').then(m => m.AutomationFormModule),
      },
      {
        path: 'dynamic-form',
        canActivate: [authGuard],
        loadChildren: () => import('./features/dynamic-form/dynamic-form.module').then(m => m.DynamicFormModule),
      },
      {
        path: '',
        canActivate: [authGuard],
        loadChildren: () => import('./features/headers/headers.module').then(m => m.HeadersModule),
      },
      {
        path: '',
        canActivate: [authGuard],
        loadChildren: () => import('./features/commission/commission.module').then(m => m.CommissionModule),
      },
      {
        path: '',
        canActivate: [authGuard],
        loadChildren: () => import('./features/dashboard/dashboard.module').then(m => m.DashboardModule),
      },
      {
        path: 'bulk-mailer',
        canActivate: [authGuard],
        loadChildren: () => import('./features/bulk-mailer/bulk-mailer.routes').then(m => m.BULK_MAILER_ROUTES)
      },
      {
        path: '',
        canActivate: [authGuard],
        loadChildren: () => import('./features/backend-users/backend-users.module').then(m => m.BackendUsersModule),
      },
      {
        path: '',
        canActivate: [authGuard],
        loadChildren: () => import('./features/mail-master/mail-master.module').then(m => m.MailMasterModule),
      },
      {
        path: 'cat',
        canActivate: [authGuard],
        loadChildren: () => import('./features/case-owner/case-owner.module').then(m => m.CaseOwnerModule),
      },
      {
        path: 'lead-form',
        canActivate: [authGuard, permissionGuard],
        data: { permission: permissionEntity.modules.lead_form },
        loadChildren: () => import('./features/lead-form/lead-form.routes').then(m => m.LEAD_FORM_ROUTES)
      },
      {
        path: 'activity',
        canActivate: [authGuard],
        loadChildren: () => import('./features/activity/activity.module').then(m => m.ActivityModule),
      },
      {
        path: 'meeting-events',
        canActivate: [authGuard],
        loadChildren: () => import('./features/meeting-events/meeting-event.routes').then(m => m.MEETING_EVENTS_ROUTES),
      },
      {
        path: 'all-leads',
        canActivate: [authGuard],
        loadChildren: () => import('./features/lead-master/lead-master.routes').then(m => m.LEAD_MASTER_ROUTES),
      },
      {
        path: 'followup-status',
        canActivate: [authGuard],
        loadChildren: () => import('./features/lead-master/followup-status/followup-status.route').then(m => m.FOLLOW_UP_STATUS_ROUTES),
      },
      {
        path: 'lead-tracking',
        canActivate: [authGuard],
        loadChildren: () => import('./features/lead-tracking/lead-tracking.routes').then(m => m.LEAD_TRACKING_ROUTES),
      },
      {
        path: 'office',
        canActivate: [authGuard],
        loadChildren: () => import('./features/branch-office/branch-office.module').then(m => m.BranchOfficeModule),
      },
      {
        path: 'conference-room',
        canActivate: [authGuard],
        loadChildren: () => import('./features/conference-room/conference-room.module').then(m => m.ConferenceRoomModule),
      },
      {
        path: 'scholarship',
        canActivate: [authGuard],
        loadChildren: () => import('./features/scholarship/scholarship.module').then(m => m.ScholarshipModule),
      },
      {
        path: 'lead-status',
        canActivate: [authGuard],
        loadChildren: () => import('./features/lead-status/lead-status.routes').then(m => m.LEAD_STATUS_ROUTES),
      },
      {
        path: 'internal-chat',
        canActivate: [authGuard],
        data: { permission: permissionEntity.modules.internal_chat },
        loadChildren: () => import('./features/internal-chat/internal-chat.routes').then(m => m.INTERNAL_CHAT_ROUTES),
      },
      {
        path: 'lead-scheduler',
        canActivate: [authGuard],
        loadChildren: () => import('./features/lead-scheduler/lead-scheduler.routes').then(m => m.LEAD_SCHEDULER_ROUTES),
      },
      {
        path: 'automation-mail',
        canActivate: [authGuard, permissionGuard],
        data: { permission: permissionEntity.modules.agent_reminder },
        loadChildren: () => import('./features/automation-mail/automation-mail.module').then(m => m.AutomationMailModule),
      },
      {
        path: 'automation-mail-form',
        canActivate: [authGuard, permissionGuard],
        data: { permission: permissionEntity.modules.mail_rule_form },
        loadChildren: () => import('./features/automation-mail-form/automation-mail-form.module').then(m => m.AutomationMailFormModule),
      },
      {
        // Search Program - Course Search and University Search as two tabs of
        // one page. The two older B2C entries ('course' and 'university'
        // below) are no longer reachable from the sidebar but are left routed
        // so their code keeps compiling and any saved link still resolves.
        path: 'programs',
        canActivate: [authGuard],
        loadChildren: () => import('./features/programs/programs-module.module').then(m => m.ProgramsModuleModule),
      },
      {
        path: 'university',
        canActivate: [authGuard],
        loadChildren: () => import('./features/university/university-module.module').then(m => m.UniversityModuleModule),
      },
      {
        path: 'course',
        canActivate: [authGuard],
        loadChildren: () => import('./features/course/course-module.module').then(m => m.CourseModuleModule),
      },
      {
        path: 'partner-survey',
        canActivate: [authGuard, permissionGuard],
        data: { permission: permissionEntity.modules.partner_survey },
        loadChildren: () => import('./features/partner-survey/partner-survay.routes').then(m => m.PARTNER_SURVEY_ROUTES)
      },
      {
        path: 'traing',
        canActivate: [authGuard],
        loadChildren: () => import('./features/university-country-traing/university-country-traing.module').then(m => m.UniversityCountryTraingModule)
      },
      {
        path: '',
        canActivate: [authGuard],
        loadChildren: () => import('./features/english-test-voucher/english-test-voucher.module').then(m => m.EnglishTestVoucherModule)
      },
      {
        path: 'mail',
        canActivate: [authGuard],
        loadChildren: () => import('./features/mail-automation/mail-automation.module').then(m => m.MailAutomationModule)
      },
      {
        path: 'rule-list',
        canActivate: [authGuard,permissionGuard],
        data: { permission: permissionEntity.modules.mail_assigner_rules },
        loadChildren: () => import('./features/rule-add/rule-add.module').then(m => m.RuleAddModule)
      },
      {  
        path: 'news',
        canActivate: [authGuard],
        loadChildren: () => import('./features/news-latter-page/news-latter-page.module').then(m => m.NewsLatterPageModule)
      }
    ]
  },
  {
    path: 'campaigns/preview',
    component: CampaignMailPreviewComponent,
    title: 'Campaigns Preview'
  },
  {
    path: 'unsubscribemail',
    component: UnsubscribeMailComponent,
    title: 'Unsubscribe Mail'
  },
  {
    path: 'unauthorised',
    component: UnauthorizedComponent,
    title: 'Unauthorized'
  },
  {
    path: 'preview/image',
    canActivate: [authGuard],
    component: ImagePreviewPageComponent,
  },
  {
    path: 'preview/pdf',
    canActivate: [authGuard],
    component: PdfPreviewPageComponent,
  },
  { path: '', redirectTo: 'login', pathMatch: 'full' },
  { path: '**', redirectTo: 'login' }
];